import { useState, useRef, useEffect } from 'react';
import { Plus, Pencil, Trash2, Lock, Unlock } from 'lucide-react';
import { useAppStore } from '../../store';
import { useSpaces, useCreateSpace, useUpdateSpace, useDeleteSpace } from '../../hooks/useSpaces';
import { useContextMenu } from '../../hooks/useContextMenu';
import type { MenuAction } from '../shared/ContextMenu';

const SPACE_COLORS = ['#3EA8FF', '#34d399', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

export function SpaceSwitcher() {
  const spaces = useSpaces();
  const currentSpaceId = useAppStore((s) => s.currentSpaceId);
  const setSpaceId = useAppStore((s) => s.setSpaceId);
  const ctxMenu = useContextMenu();
  const createSpace = useCreateSpace();
  const updateSpace = useUpdateSpace();
  const deleteSpace = useDeleteSpace();

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(SPACE_COLORS[2]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const editRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingId && editRef.current) {
      editRef.current.focus();
      editRef.current.select();
    }
  }, [editingId]);

  const commitRename = () => {
    if (editingId && editName.trim()) {
      updateSpace.mutate({ id: editingId, changes: { name: editName.trim() } });
    }
    setEditingId(null);
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    createSpace.mutate({ name: newName.trim(), color: newColor }, {
      onSuccess: (space) => {
        setSpaceId(space.id);
        setShowCreate(false);
        setNewName('');
      },
    });
  };

  const handleContextMenu = (e: React.MouseEvent, space: typeof spaces[0]) => {
    e.preventDefault();
    const items: MenuAction[] = [
      {
        label: 'Rename',
        icon: <Pencil size={13} />,
        onClick: () => {
          setEditingId(space.id);
          setEditName(space.name);
        },
      },
      {
        label: space.private ? 'Make visible' : 'Make private',
        icon: space.private ? <Unlock size={13} /> : <Lock size={13} />,
        onClick: () => {
          updateSpace.mutate({ id: space.id, changes: { private: !space.private } });
        },
      },
    ];

    if (spaces.length > 1) {
      items.push({
        label: 'Delete',
        icon: <Trash2 size={13} />,
        danger: true,
        onClick: () => {
          const others = spaces.filter(s => s.id !== space.id);
          const target = others[0];
          if (confirm(`Delete "${space.name}"? All tasks will move to "${target.name}".`)) {
            deleteSpace.mutate({ id: space.id, targetSpaceId: target.id }, {
              onSuccess: () => {
                if (currentSpaceId === space.id) setSpaceId(target.id);
              },
            });
          }
        },
      });
    }

    ctxMenu.open(e.clientX, e.clientY, items);
  };

  return (
    <div className="flex items-center gap-1">
      {spaces.map((space) => {
        const isActive = space.id === currentSpaceId;
        const isEditing = editingId === space.id;

        if (isEditing) {
          return (
            <form
              key={space.id}
              onSubmit={(e) => { e.preventDefault(); commitRename(); }}
              className="flex items-center"
            >
              <input
                ref={editRef}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => { if (e.key === 'Escape') setEditingId(null); }}
                maxLength={30}
                className="bg-bg-raised border border-accent rounded-md px-2 py-0.5 text-caption font-medium text-text-primary
                  focus:outline-none w-24"
                style={{ color: space.color }}
              />
            </form>
          );
        }

        return (
          <button
            key={space.id}
            onClick={() => setSpaceId(space.id)}
            onDoubleClick={() => { setEditingId(space.id); setEditName(space.name); }}
            onContextMenu={(e) => handleContextMenu(e, space)}
            className={`px-2.5 py-1 rounded-md text-caption font-medium transition-all duration-150
              ${isActive
                ? 'text-text-primary'
                : 'text-text-muted hover:text-text-secondary'
              }`}
            style={isActive ? {
              backgroundColor: `${space.color}15`,
              color: space.color,
              boxShadow: `inset 0 -2px 0 ${space.color}`,
            } : undefined}
          >
            {space.name}
          </button>
        );
      })}

      {spaces.length < 3 && !showCreate && (
        <button
          onClick={() => setShowCreate(true)}
          className="w-6 h-6 flex items-center justify-center rounded-md text-text-muted hover:text-text-secondary hover:bg-bg-raised transition-colors"
          title="Create space"
        >
          <Plus size={14} />
        </button>
      )}

      {showCreate && (
        <form onSubmit={handleCreate} className="flex items-center gap-1.5 ml-1">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && setShowCreate(false)}
            placeholder="Name..."
            maxLength={30}
            className="bg-bg-raised border border-border-default rounded-md px-2 py-0.5 text-caption text-text-primary
              placeholder:text-text-muted focus:outline-none focus:border-accent w-24"
          />
          <div className="flex gap-0.5">
            {SPACE_COLORS.slice(0, 5).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setNewColor(c)}
                className={`w-4 h-4 rounded-full transition-transform ${newColor === c ? 'scale-125 ring-1 ring-white/50' : ''}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <button
            type="submit"
            disabled={!newName.trim()}
            className="text-caption font-medium text-accent hover:text-accent/80 disabled:opacity-40 disabled:cursor-not-allowed px-1"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(false)}
            className="text-caption text-text-muted hover:text-text-secondary px-1"
          >
            Cancel
          </button>
        </form>
      )}
    </div>
  );
}
